import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import styles from "./AdminDashboard.module.css";

const STORAGE_KEY = "adminDashboardConfig";
const ItemType = "CARD";

const DEFAULT_PAGES = [
  { id: "users", path: "/admin/users", icon: "bi-people", title: "Пользователи", desc: "Просмотр и управление пользователями", section: "main" },
  { id: "roles", path: "/admin/roles", icon: "bi-shield-lock", title: "Роли", desc: "Создание и назначение ролей", section: "main" },
  { id: "permissions", path: "/admin/permissions", icon: "bi-key", title: "Права доступа", desc: "Гранулярное управление разрешениями", section: "main" },
  { id: "logs", path: "/admin/logs", icon: "bi-clipboard-data", title: "Журналы", desc: "Мониторинг активности и событий", section: "main" },
  { id: "backups", path: "/admin/backups", icon: "bi-cloud-arrow-up", title: "Резервные копии", desc: "Создание и восстановление резервных копий", section: "archive" },
];

function Card({ page, index, section, moveWithin, moveToSection }) {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: ItemType,
    hover(item) {
      if (item.id === page.id || item.section !== section) return;
      moveWithin(item.index, index, section);
      item.index = index;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { id: page.id, index, section },
    end(item, monitor) {
      const dropRes = monitor.getDropResult();
      if (dropRes?.section && dropRes.section !== section) {
        moveToSection(page.id, dropRes.section);
      }
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));

  return (
    <Link
      to={page.path}
      ref={ref}
      className={styles.card}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <i className={`bi ${page.icon} ${styles.cardIcon}`} />
      <div className={styles.cardText}>
        <h4>{page.title}</h4>
        {page.desc && <p className={styles.desc}>{page.desc}</p>}
      </div>
    </Link>
  );
}

function Section({ title, pages, sectionKey, moveWithin, moveToSection }) {
  const [, drop] = useDrop({
    accept: ItemType,
    drop: () => ({ section: sectionKey }),
  });

  return (
    <section className={styles.section} ref={drop}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      {pages.length ? (
        <div className={styles.cards}>
          {pages.map((p, i) => (
            <Card
              key={p.id}
              page={p}
              index={i}
              section={sectionKey}
              moveWithin={moveWithin}
              moveToSection={moveToSection}
            />
          ))}
        </div>
      ) : (
        <p className={styles.empty}>Нет элементов в этом разделе</p>
      )}
    </section>
  );
}

export default function AdminDashboard() {
  const [pages, setPages] = useState([]);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    let stored = [];
    try {
      if (raw) stored = JSON.parse(raw);
    } catch {}
    const merged = DEFAULT_PAGES.map((p) => {
      const found = stored.find((x) => x.id === p.id);
      return found ? { ...p, section: found.section } : p;
    });
    setPages(merged);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pages));
  }, [pages]);

  const moveWithin = useCallback((fromIdx, toIdx, section) => {
    setPages((prev) => {
      const sect = prev.filter((p) => p.section === section);
      const card = sect[fromIdx];
      const updated = [...sect];
      updated.splice(fromIdx, 1);
      updated.splice(toIdx, 0, card);
      const result = [];
      prev.forEach((p) => {
        if (p.section !== section) result.push(p);
        else result.push(updated.shift());
      });
      return result;
    });
  }, []);

  const moveToSection = useCallback((id, toSection) => {
    setPages((prev) =>
      prev.map((p) => (p.id === id ? { ...p, section: toSection } : p))
    );
  }, []);

  const mainPages = pages.filter((p) => p.section === "main");
  const archivePages = pages.filter((p) => p.section === "archive");

  return (
    <div className={styles.dashboard}>
      <header className={styles.header}>
        <div className={styles.headerIcon}>
          <i className="bi bi-sliders" />
        </div>
        <div className={styles.headerText}>
          <h1>Админская панель</h1>
          <p>Организуйте и управляйте страницами администратора</p>
        </div>
      </header>

      <DndProvider backend={HTML5Backend}>
        <Section
          title="Основная секция"
          pages={mainPages}
          sectionKey="main"
          moveWithin={moveWithin}
          moveToSection={moveToSection}
        />
        <Section
          title="Архив"
          pages={archivePages}
          sectionKey="archive"
          moveWithin={moveWithin}
          moveToSection={moveToSection}
        />
      </DndProvider>
    </div>
  );
}
